
import { Link } from 'react-router-dom';

export default function HealthManagementPlan() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">ホーム</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li>
                <Link to="/health-support" className="text-gray-500 hover:text-gray-700 cursor-pointer">健康サポート</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li className="text-gray-900">健康管理計画</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-50 to-teal-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/b474bce80e9d625db0ebe8c33faf55ef.jpg"
            alt="健康管理計画"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            健康管理<span className="text-green-600">計画</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            個人の状況に合わせた健康管理プランを作成し、継続的にフォローいたします
          </p>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">あなただけの健康管理プラン</h2>
              <p className="text-lg text-gray-600 mb-8">
                一人ひとりの健康状態、生活習慣、目標に合わせて、薬剤師が個別の健康管理計画を作成します。
                定期的なフォローアップにより、計画の見直しと改善を継続的にサポートします。
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">個別化されたプラン</h3>
                    <p className="text-gray-600">あなたの健康状態と生活スタイルに完全に合わせた計画</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">継続的なサポート</h3>
                    <p className="text-gray-600">定期的な見直しと調整で長期的な健康維持をサポート</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">多角的なアプローチ</h3>
                    <p className="text-gray-600">薬物療法、食事、運動、生活習慣を総合的に管理</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Pharmacist%20creating%20detailed%20health%20management%20plan%20with%20patient%2C%20personalized%20wellness%20planning%20session%20with%20health%20charts%20and%20goal%20setting%2C%20professional%20healthcare%20consultation%20about%20comprehensive%20health%20strategy&width=500&height=600&seq=health-plan-consultation&orientation=portrait"
                alt="健康管理計画相談"
                className="w-full max-w-md h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Plan Components */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">健康管理計画の構成要素</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-medicine-bottle-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">薬物療法管理</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 服薬スケジュール最適化</li>
                <li>• 副作用モニタリング</li>
                <li>• 薬効評価</li>
                <li>• 相互作用チェック</li>
                <li>• 服薬アドヒアランス向上</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-restaurant-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">栄養管理</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 個別栄養評価</li>
                <li>• 食事プラン作成</li>
                <li>• 栄養素バランス調整</li>
                <li>• サプリメント指導</li>
                <li>• 食事記録分析</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-run-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">運動プログラム</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 体力レベル評価</li>
                <li>• 個別運動メニュー</li>
                <li>• 運動強度設定</li>
                <li>• 進捗モニタリング</li>
                <li>• 安全性確保</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">生活習慣改善</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 睡眠パターン改善</li>
                <li>• ストレス管理</li>
                <li>• 禁煙・節酒支援</li>
                <li>• 生活リズム調整</li>
                <li>• 環境要因改善</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Plan Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">健康管理プランの種類</h2>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-lg p-8 border border-blue-100">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">予防重視プラン</h3>
              <p className="text-gray-600 mb-4">健康な方向けの疾病予防に特化したプラン</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 定期健診スケジュール管理</li>
                <li>• 生活習慣病予防対策</li>
                <li>• 免疫力向上プログラム</li>
                <li>• ストレス管理指導</li>
                <li>• 栄養バランス最適化</li>
                <li>• 適度な運動習慣構築</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl shadow-lg p-8 border border-orange-100">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-heart-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">疾患管理プラン</h3>
              <p className="text-gray-600 mb-4">慢性疾患をお持ちの方向けの管理プラン</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 薬物療法最適化</li>
                <li>• 症状モニタリング</li>
                <li>• 合併症予防対策</li>
                <li>• 食事療法指導</li>
                <li>• 運動療法プログラム</li>
                <li>• 医療機関連携サポート</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl shadow-lg p-8 border border-green-100">
              <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">高齢者サポートプラン</h3>
              <p className="text-gray-600 mb-4">高齢者の方向けの総合的なサポートプラン</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• フレイル・サルコペニア予防</li>
                <li>• 認知機能維持プログラム</li>
                <li>• 転倒予防対策</li>
                <li>• 多剤併用管理</li>
                <li>• 栄養状態改善</li>
                <li>• 社会参加促進支援</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">健康管理計画作成の流れ</h2>
          <div className="grid md:grid-cols-6 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">初回面談</h3>
              <p className="text-gray-600">健康状態、生活習慣、目標を詳しくヒアリング</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">健康評価</h3>
              <p className="text-gray-600">現在の健康状態を多角的に評価・分析</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">計画作成</h3>
              <p className="text-gray-600">個別の健康管理計画を詳細に作成</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">計画説明</h3>
              <p className="text-gray-600">計画内容を詳しく説明し、実行方法を指導</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">5</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">実行サポート</h3>
              <p className="text-gray-600">計画実行を継続的にサポート・指導</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">6</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">評価・見直し</h3>
              <p className="text-gray-600">定期的に効果を評価し、計画を調整</p>
            </div>
          </div>
        </div>
      </section>

      {/* Monitoring Tools */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">モニタリングツール</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg p-6 border border-blue-100">
              <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-chart-line text-lg"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">健康記録シート</h3>
              <p className="text-gray-600 text-sm">日々の体調、服薬状況、運動記録を簡単に記録</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 border border-green-100">
              <div className="w-10 h-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-check-line text-lg"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">定期チェック表</h3>
              <p className="text-gray-600 text-sm">血圧、体重、血糖値などの定期測定をサポート</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl shadow-lg p-6 border border-orange-100">
              <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-restaurant-2-line text-lg"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">食事記録アプリ</h3>
              <p className="text-gray-600 text-sm">栄養バランスと摂取カロリーを簡単に管理</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg p-6 border border-purple-100">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-notification-line text-lg"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">リマインダー機能</h3>
              <p className="text-gray-600 text-sm">服薬時間、運動時間、測定時間をお知らせ</p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">成功事例</h2>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Aさん（50代男性）</h3>
                  <p className="text-sm text-gray-500">糖尿病予防プラン</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                健康診断で血糖値の異常を指摘され、糖尿病予防プランを開始。
                食事指導と運動プログラムにより、6ヶ月でHbA1cが7.2%から6.1%に改善。
              </p>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>改善結果：</strong>体重-8kg、HbA1c-1.1%、血圧正常化
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Bさん（70代女性）</h3>
                  <p className="text-sm text-gray-500">高齢者サポートプラン</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                フレイルの兆候があり、高齢者サポートプランを実施。
                筋力トレーニングと栄養指導により、歩行速度と筋力が大幅に改善。
              </p>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>改善結果：</strong>歩行速度+20%、握力+15%、転倒リスク軽減
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Cさん（40代女性）</h3>
                  <p className="text-sm text-gray-500">疾患管理プラン</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                高血圧と脂質異常症の管理プランを実施。
                薬物療法の最適化と生活習慣改善により、両疾患とも良好にコントロール。
              </p>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>改善結果：</strong>血圧正常化、LDL-40mg/dl、薬剤数減少
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">あなたの健康管理計画を始めませんか？</h2>
          <p className="text-xl text-green-100 mb-8">
            薬剤師があなたに最適な健康管理プランを作成し、継続的にサポートいたします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/health-support"
              className="bg-white text-green-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              健康サポートに戻る
            </Link>
            <Link 
              to="/"
              className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-green-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              ホームに戻る
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-consultation/health-checkup-explanation" className="hover:text-white cursor-pointer">健康診断結果解説</Link></li>
                <li><Link to="/health-consultation/lifestyle-disease-prevention" className="hover:text-white cursor-pointer">生活習慣病予防</Link></li>
                <li><Link to="/health-consultation/medication-therapy-consultation" className="hover:text-white cursor-pointer">薬物療法相談</Link></li>
                <li><Link to="/health-consultation/health-management-plan" className="hover:text-white cursor-pointer">健康管理計画</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
